import {
  AuthenticationResult,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  m365CopilotScopes,
  type AiTokenOptions,
  type M365AuthOptions,
} from "@onetasklist/shared";

/**
 * The multi-tenant authority. OneTaskList owns a single Entra app registration and every customer
 * signs in through it with their own work account, so the authority is never a tenant we are told
 * about — "organizations" lets Entra route the sign-in to whichever tenant the account belongs to,
 * while still keeping personal Microsoft accounts (which have no Copilot) out.
 */
const authority = "https://login.microsoftonline.com/organizations";

/**
 * One MSAL client per registration for the life of the page. MSAL keeps the account and its
 * refresh token in this tab's sessionStorage, so a reload can renew silently while a new tab
 * still has to sign in.
 */
const clients = new Map<string, Promise<PublicClientApplication>>();

/**
 * The registrations this tab has used, so sign-out can find MSAL's cache after a reload, before
 * the assistant has opened and created a client.
 */
const usedClientIdsKey = "onetasklist.ai.m365ClientIds";

function usedClientIds(): string[] {
  try {
    const ids: unknown = JSON.parse(sessionStorage.getItem(usedClientIdsKey) ?? "[]");
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function rememberClientId(clientId: string) {
  const ids = usedClientIds();
  if (ids.includes(clientId)) return;
  try {
    sessionStorage.setItem(usedClientIdsKey, JSON.stringify([...ids, clientId]));
  } catch {
    // Then sign-out clears only what this page load used.
  }
}

function clientFor(clientId: string): Promise<PublicClientApplication> {
  let client = clients.get(clientId);
  if (!client) {
    rememberClientId(clientId);
    const msal = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
    });
    client = msal.initialize().then(() => msal);
    clients.set(clientId, client);
  }
  return client;
}

const scopesFor = (options: M365AuthOptions) =>
  options.scopes?.length ? options.scopes : m365CopilotScopes;

export async function signInToM365Copilot(
  options: M365AuthOptions,
): Promise<AuthenticationResult> {
  const scopes = scopesFor(options);
  const msal = await clientFor(options.clientId);
  const login = await msal.loginPopup({ scopes, prompt: "select_account" });
  if (!login.account) return login;
  msal.setActiveAccount(login.account);
  try {
    return await msal.acquireTokenSilent({ scopes, account: login.account });
  } catch {
    return await msal.acquireTokenPopup({ scopes, account: login.account });
  }
}

export interface M365RenewedToken {
  accessToken: string;
  /** When the token stops working, in milliseconds since the epoch. */
  expiresOn: number;
}

/**
 * A current token for the account this tab already signed in with, without a prompt: the cached
 * one while it lasts, otherwise a new one from the refresh token. Null when the tab has no account,
 * or Entra wants the user back (consent, MFA, a revoked session), so the caller asks them to
 * connect again rather than failing a chat.
 */
export async function renewM365CopilotToken(
  options: M365AuthOptions,
  { forceRefresh = false }: AiTokenOptions = {},
): Promise<M365RenewedToken | null> {
  try {
    const msal = await clientFor(options.clientId);
    const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
    if (!account) return null;
    // forceRefresh skips a cached token Graph has already turned away.
    const result = await msal.acquireTokenSilent({
      scopes: scopesFor(options),
      account,
      forceRefresh,
    });
    if (!result.accessToken) return null;
    return {
      accessToken: result.accessToken,
      expiresOn: result.expiresOn?.getTime() ?? Date.now() + 60 * 60 * 1000,
    };
  } catch {
    return null;
  }
}

/**
 * Forgets the Copilot sign-in in this tab, for every registration it used. MSAL v2 has no plain
 * cache clear: a logout whose navigation is refused clears the cached accounts and tokens and
 * stays on the page. Entra's own session is left alone. Signing out of OneTaskList shouldn't
 * sign the user out of Microsoft 365 everywhere else.
 */
export async function forgetM365CopilotSignIns(): Promise<void> {
  const ids = new Set([...clients.keys(), ...usedClientIds()]);
  await Promise.all(
    [...ids].map(async (clientId) => {
      try {
        const msal = await clientFor(clientId);
        await msal.logoutRedirect({ onRedirectNavigate: () => false });
      } catch {
        // The cache is in sessionStorage and goes when the tab closes.
      }
    }),
  );
  clients.clear();
}
